import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import MenuPlan from 'classes/MenuPlan/Detail/MenuPlan';
import DateSelect from 'components/desktop/MenuPlan/CreateMenuPlanForm/DateSelect';
import RowManagement from 'components/desktop/MenuPlan/CreateMenuPlanForm/RowManagement';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import InputTranslateButton from 'components/desktop/_general/Input/InputExtensions/InputTranslateButton/InputTranslateButton';
import InputFieldSimple from 'components/desktop/_general/Input/InputFields/InputFieldSimple';
import RoundedInput from 'components/desktop/_general/Input/RoundedInput/RoundedInput';
import SimpleInput from 'components/desktop/_general/Input/SimpleInput/SimpleInput';
import LanguageSwitch from 'components/desktop/_general/LanguageSwitch/LanguageSwitch';
import ENTITLEMENTS from 'enums/entitlements';
import clone from 'functions/clone';
import { areEntitlementsInUserEntitlements } from 'functions/tokenFunctions';
import { usePermissions } from 'hooks/usePermissions';
import { RootState } from 'reducers/rootReducer';
import { regexValidationCalories } from 'regex/validation/Calories';
import { regexValidationPrice } from 'regex/validation/Price';
import { Language } from 'types/Language/Language';

interface IProps {
	menuPlan: MenuPlan;
	selectedLanguage: Language;
	setMenuPlan: (input: MenuPlan) => void;
	setSelectedLanguage: (input: Language) => void;
}

const Header = (props: IProps) => {
	const { isMenuPlanEditor } = usePermissions();
	const isDisabled = props.menuPlan.isDisabled();
	const reduxLanguages: Language[] = useSelector((state: RootState) => state.languages);
	const reduxCurrency: string = useSelector((state: RootState) => state.currency);
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	useEffect(() => {
		// Get today's date
		const today = new Date();

		// Find start of *next* week (Monday)
		const day = today.getDay(); // 0=Sun,1=Mon,...,6=Sat
		const daysUntilNextMonday = (8 - day) % 7 || 7;
		const nextMonday = new Date(today);
		nextMonday.setDate(today.getDate() + daysUntilNextMonday + 1);
		nextMonday.setHours(0, 0, 0, 0);

		// Friday of that week
		const nextFriday = new Date(nextMonday);
		nextFriday.setDate(nextMonday.getDate() + 4);

		// Update state
		changeStartDate(nextMonday);
		changeEndDate(nextFriday);
	}, []);

	const changeStartDate = (input: Date | null): void => {
		if (!input) return;
		props.menuPlan.startDate = input;
		props.setMenuPlan(clone(props.menuPlan));
	};

	const changeEndDate = (input: Date | null): void => {
		if (!input) return;
		props.menuPlan.endDate = input;
		props.setMenuPlan(clone(props.menuPlan));
	};

	const changeCaloriesLimit = (input: number): void => {
		props.menuPlan.setCaloriesLimit(input);
		props.setMenuPlan(clone(props.menuPlan));
	};

	const changeCaloriesPerDayLimit = (input: string): void => {
		props.menuPlan.setCaloriesLimitPerDay(Number(input));
		props.setMenuPlan(clone(props.menuPlan));
	};

	const changePriceLimit = (input: number): void => {
		props.menuPlan.setPriceLimit(input);
		props.setMenuPlan(clone(props.menuPlan));
	};

	const changePricePerDayLimit = (input: string): void => {
		props.menuPlan.setPriceLimitPerDay(Number(input));
		props.setMenuPlan(clone(props.menuPlan));
	};

	const handleSelectLanguage = (_e: any, selectedLanguage: string) => {
		props.setSelectedLanguage(
			reduxLanguages[
				reduxLanguages.findIndex((element) => element.cultureCode == selectedLanguage)
			]
		);
	};

	const handleChangeTitle = (input: string): void => {
		const value: string = input;
		props.menuPlan.title = {
			...props.menuPlan.title,
			...{
				[props.selectedLanguage.cultureCode]: value,
			},
		};
		props.setMenuPlan(clone(props.menuPlan));
	};

	const handleTranslateAll = async () => {
		await props.menuPlan.translateMenuPlan(
			reduxCultureCode,
			props.selectedLanguage.cultureCode
		);
		props.setMenuPlan(clone(props.menuPlan));
	};

	const handleTranslateInput = async (_name: string) => {
		await props.menuPlan.translateTitle(reduxCultureCode, props.selectedLanguage.cultureCode);
		props.setMenuPlan(clone(props.menuPlan));
	};

	const isTranslatable = (): boolean => {
		if (!isMenuPlanEditor || isDisabled) return false;
		if (reduxLanguages && reduxCultureCode !== undefined) {
			if (reduxCultureCode === props.selectedLanguage.cultureCode) {
				return false;
			} else {
				return true;
			}
		}
		return false;
	};

	const [useWeekPicker, setUseWeekPicker] = useState(false);

	const renderDateSelects = (): JSX.Element => {
		return (
			<>
				<div className="mb-2">
					<label>
						<input
							type="checkbox"
							checked={useWeekPicker}
							onChange={(e) => setUseWeekPicker(e.target.checked)}
						/>{' '}
						{t('_general:USE_WEEK_PICKER')}
					</label>
				</div>
				<div className="row">
					<div className={!useWeekPicker ? 'col-6' : ''}>
						<DateSelect
							label={t('menuPlan:START')}
							value={props.menuPlan.startDate}
							changeValue={(monday) => {
								if (useWeekPicker && monday) {
									const friday = new Date(monday);
									friday.setDate(monday.getDate() + 5);
									monday.setDate(monday.getDate() + 1);
									changeStartDate(monday);
									changeEndDate(friday);
								} else {
									changeStartDate(monday);
								}
							}}
							weekPicker={useWeekPicker}
						/>
					</div>
					{!useWeekPicker && (
						<div className="col-6">
							<DateSelect
								label={t('menuPlan:END')}
								value={props.menuPlan.endDate}
								changeValue={changeEndDate}
							/>
						</div>
					)}
				</div>
			</>
		);
	};

	const addRow = (): void => {
		props.menuPlan.addRow();
		props.setMenuPlan(clone(props.menuPlan));
	};

	const renderLanguageSwitch = (): JSX.Element => {
		return (
			<LanguageSwitch
				dataComponentType="menu-plan-language-switcher"
				handleSelectLanguage={handleSelectLanguage}
				selectedLanguage={props.selectedLanguage.cultureCode}
				translatable={!isDisabled}
				onTranslateClick={handleTranslateAll}
				entryLanguage={reduxCultureCode}
				content={
					<div className="row mt-2">
						<div className="col-6">
							<label>{t('menuPlan:TITLE')}</label>
							<div className="input-group">
								<SimpleInput
									dataComponentType="menu-plan-title-input"
									value={
										props.menuPlan.title?.[
											props.selectedLanguage.cultureCode
										] ?? ''
									}
									handleChange={handleChangeTitle}
									disabled={!isMenuPlanEditor || isDisabled}
									errorKey="TitleTranslations"
								/>
								<RenderIf
									entitlements={[ENTITLEMENTS.TRANSLATION_SERVICE]}
									condition={isTranslatable()}
								>
									<InputTranslateButton
										name="titleTranslations"
										onClick={handleTranslateInput}
									/>
								</RenderIf>
							</div>
						</div>
						<div className="col-6">
							<div
								style={{
									maxHeight: '12rem',
									overflowY: 'auto',
									marginBottom: '0.5rem',
								}}
							>
								<RowManagement
									selectedCultureCode={props.selectedLanguage.cultureCode}
									entryLanguage={reduxCultureCode}
									languages={reduxLanguages}
									menuPlan={props.menuPlan}
									setMenuPlan={props.setMenuPlan}
									disabled={!isMenuPlanEditor || isDisabled}
								/>
							</div>
							<button className="btn btn-outline-primary" onClick={addRow}>
								<FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
								{t('menuPlan:NEW_ROW')}
							</button>
						</div>
					</div>
				}
			/>
		);
	};

	const getClassNameOfCaloriesInputField = (): string => {
		return areEntitlementsInUserEntitlements([ENTITLEMENTS.PRICE]) ? 'col-6' : 'col-12';
	};

	const renderLimits = (): JSX.Element => {
		return (
			<>
				<label>{t('menuPlan:MAX_LIMIT_PER_PERSON')}</label>
				<div className="row">
					<div className={getClassNameOfCaloriesInputField()}>
						<div className="input-group mb-3">
							<RoundedInput
								dataComponentType="menu-plan-calories-limit"
								disabled={!isMenuPlanEditor || isDisabled}
								handleChange={changeCaloriesLimit}
								value={props.menuPlan.caloriesLimit ?? 0}
							/>
							<span className="input-group-text">{t('measurments:CAL')}</span>
						</div>
					</div>
					<RenderIf entitlements={[ENTITLEMENTS.PRICE]}>
						<div className="col-6">
							<div className="input-group ">
								<RoundedInput
									dataComponentType="menu-plan-price-limit"
									disabled={!isMenuPlanEditor || isDisabled}
									handleChange={changePriceLimit}
									value={props.menuPlan.priceLimit ?? 0}
								/>
								<span className="input-group-text">{reduxCurrency}</span>
							</div>
						</div>
					</RenderIf>
				</div>
				<label>{t('menuPlan:MAX_LIMIT_PER_PERSON_PER_DAY')}</label>
				<div className="row">
					<div className={getClassNameOfCaloriesInputField()}>
						<div className="input-group mb-3">
							<InputFieldSimple
								defaultValue={String(props.menuPlan.stringifyCaloriesLimitPerDay())}
								disabled={true}
								handleValueChange={changeCaloriesPerDayLimit}
								validateRegex={regexValidationCalories}
							/>
							<span className="input-group-text">{t('measurments:CAL')}</span>
						</div>
					</div>
					<RenderIf entitlements={[ENTITLEMENTS.PRICE]}>
						<div className="col-6">
							<div className="input-group mb-3">
								<InputFieldSimple
									defaultValue={props.menuPlan.stringifyPriceLimitPerDay()}
									disabled={true}
									handleValueChange={changePricePerDayLimit}
									validateRegex={regexValidationPrice}
								/>
								<span className="input-group-text">{reduxCurrency}</span>
							</div>
						</div>
					</RenderIf>
				</div>
			</>
		);
	};

	return (
		<>
			<div>
				{renderLanguageSwitch()}
				<hr />
				{renderDateSelects()}
				<hr />
				{renderLimits()}
			</div>
		</>
	);
};

export default Header;
